'use client';
import ReactDOM from 'react-dom';

import { useTranslation } from '@/lib/i18n/i18n.client';

import { gatedContentRootId } from '@/components/atoms/layout/CMSBlogLayout';
import useCaptureUserDetails from '@/components/organisms/gated-content/useCaptureUserDetails';
import useGatedContentStyleHack from '@/components/organisms/gated-content/useGatedContentStyleHack';

import Button from '@/decodo/components/atoms/Button';
import { Input } from '@/decodo/components/atoms/Input';
import RichTextFieldStrapi from '@/decodo/components/atoms/RichTextField/RichTextFieldStrapi';

import { CaptureUserDetailsProps } from './types';

/**
 * NOTE: For this element to be visible, elements with ids `blog-main-content`
 * and `<GatedContentTargetContainer/>` component must be present in the DOM.
 */
const GatedContent = (props: CaptureUserDetailsProps) => {
  const { texts } = useTranslation();
  const { errors, handleSubmit, values, getFieldProps, isLoading, gateId } =
    useCaptureUserDetails(props);

  const { hasApplied } = useGatedContentStyleHack({ gateId });

  // until the check for gate clearance has been made, render an empty element that
  // serves as a wall when scanning through elements to find children that need to be hidden
  if (!hasApplied) return <div id={gateId} />;

  // once the check is done, render the content in a portal targeting the root container
  const Content = (
    <div className='decodo relative z-40 w-full' id={gateId}>
      <div className='to-base-white -mt-24 h-24 bg-gradient-to-b from-transparent' />
      <div className='bg-base-white flex w-full justify-center'>
        <section
          className='bg-base-white mx-5 my-8 flex h-min w-full flex-col justify-center text-gray-950 md:mx-[80px] md:my-12 md:max-w-5xl md:text-center'
          id={gateId}
        >
          <div className='mb-4 '>
            <RichTextFieldStrapi block={props.title} />
          </div>
          <div className='text-body-l mb-4 min-h-16 text-gray-500'>
            <RichTextFieldStrapi block={props.description} />
          </div>
          <div className='mx-auto'>
            <form
              className='flex flex-col text-left'
              onSubmit={handleSubmit}
              data-test-id='gated-content-form'
            >
              <Input
                placeholder={texts.gated_content.placeholder_email}
                label={texts.gated_content.label_email}
                required
                error={errors.email || ''}
                {...getFieldProps('email')}
                classNames={{
                  label: {
                    root: 'text-gray-500 uppercase text-label-text-l font-serif',
                  },
                  bottomText: { root: 'h-6' },
                }}
              />
              <Button
                classNames={{
                  root: 'w-full mb-6',
                }}
                disabled={!values.email || isLoading}
              >
                <RichTextFieldStrapi block={props.submitButtonText} />
              </Button>
            </form>
            <div className='text-body-m text-gray-500'>
              <RichTextFieldStrapi block={props.termsAndConditions} />
            </div>
          </div>
        </section>
      </div>
    </div>
  );

  const gatedContentContainer = document.querySelector(
    `#${gatedContentRootId}`
  );

  if (!gatedContentContainer) {
    console.error(
      'Gated content container not found for gateId:',
      gateId,
      '. Skipping.'
    );
    return null;
  }

  return ReactDOM.createPortal(Content, gatedContentContainer);
};

export default GatedContent;
